import React, { useState, useEffect, useCallback } from 'react';
import './SnakeGame.css';

const GRID_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_DIRECTION = { x: 0, y: -1 };
const GAME_SPEED = 150;

const SnakeGame = ({ onGameEnd }) => {
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [directionQueue, setDirectionQueue] = useState([]);
  const [food, setFood] = useState({ x: 15, y: 15 });
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);

  const generateFood = useCallback((currentSnake = snake) => {
    let newFood;
    let attempts = 0;
    const maxAttempts = 100; // Prevent infinite loop
    
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE)
      };
      attempts++;
    } while (
      attempts < maxAttempts &&
      currentSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y)
    );
    
    return newFood;
  }, [snake]);

  const checkCollision = useCallback((head, snakeArray) => {
    // Wall collision
    if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
      return true;
    }
    // Self collision
    for (let segment of snakeArray) {
      if (head.x === segment.x && head.y === segment.y) {
        return true;
      }
    }
    return false;
  }, []);

  const moveSnake = useCallback(() => {
    if (gameOver || !gameStarted) return;

    setDirectionQueue(currentQueue => {
      let newDirection = direction;
      let updatedQueue = [...currentQueue];
      
      // Process next direction from queue if available
      if (updatedQueue.length > 0) {
        newDirection = updatedQueue.shift();
        setDirection(newDirection);
      }

      setSnake(currentSnake => {
        const newSnake = [...currentSnake];
        const head = { x: newSnake[0].x + newDirection.x, y: newSnake[0].y + newDirection.y };

        if (checkCollision(head, newSnake)) {
          setGameOver(true);
          return currentSnake;
        }

        newSnake.unshift(head);

        // Check if food is eaten
        if (head.x === food.x && head.y === food.y) {
          setScore(prev => prev + 1);
          setFood(generateFood(newSnake));
        } else {
          newSnake.pop();
        }

        return newSnake;
      });

      return updatedQueue;
    });
  }, [direction, food, gameOver, gameStarted, checkCollision, generateFood]);

  const handleKeyPress = useCallback((e) => {
    if (!gameStarted) {
      setGameStarted(true);
      return;
    }

    const addDirectionToQueue = (newDirection) => {
      setDirectionQueue(currentQueue => {
        // Get the last direction (either current direction or last in queue)
        const lastDirection = currentQueue.length > 0 
          ? currentQueue[currentQueue.length - 1] 
          : direction;
        
        // Check if new direction is opposite to the last direction
        const isOpposite = (
          (newDirection.x === 1 && lastDirection.x === -1) ||
          (newDirection.x === -1 && lastDirection.x === 1) ||
          (newDirection.y === 1 && lastDirection.y === -1) ||
          (newDirection.y === -1 && lastDirection.y === 1)
        );
        
        // Don't add if it's the same as last direction or opposite
        if (!isOpposite && 
            (lastDirection.x !== newDirection.x || lastDirection.y !== newDirection.y) &&
            currentQueue.length < 2) { // Limit queue size to prevent excessive buffering
          return [...currentQueue, newDirection];
        }
        
        return currentQueue;
      });
    };

    switch (e.key) {
      case 'ArrowUp':
        addDirectionToQueue({ x: 0, y: -1 });
        break;
      case 'ArrowDown':
        addDirectionToQueue({ x: 0, y: 1 });
        break;
      case 'ArrowLeft':
        addDirectionToQueue({ x: -1, y: 0 });
        break;
      case 'ArrowRight':
        addDirectionToQueue({ x: 1, y: 0 });
        break;
      case 'Escape':
        handleGameEnd();
        break;
      default:
        break;
    }
  }, [direction, directionQueue, gameStarted]);

  const handleGameEnd = () => {
    if (onGameEnd) {
      onGameEnd();
    }
  };

  const restartGame = () => {
    setSnake(INITIAL_SNAKE);
    setDirection(INITIAL_DIRECTION);
    setDirectionQueue([]);
    setFood(generateFood(INITIAL_SNAKE));
    setGameOver(false);
    setScore(0);
    setGameStarted(false);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    const gameInterval = setInterval(moveSnake, GAME_SPEED);
    return () => clearInterval(gameInterval);
  }, [moveSnake]);

  return (
    <div className="snake-game">
      <div className="game-header">
        <h1 className="game-title">NORTONEO SNAKE</h1>
        <div className="game-score">SCORE: {score}</div>
      </div>
      
      <div className="game-board">
        {Array.from({ length: GRID_SIZE * GRID_SIZE }, (_, index) => {
          const x = index % GRID_SIZE;
          const y = Math.floor(index / GRID_SIZE);
          
          const isSnake = snake.some(segment => segment.x === x && segment.y === y);
          const isSnakeHead = snake[0]?.x === x && snake[0]?.y === y;
          const isFood = food.x === x && food.y === y;
          
          return (
            <div
              key={index}
              className={`game-cell ${isSnake ? 'snake' : ''} ${isSnakeHead ? 'snake-head' : ''} ${isFood ? 'food' : ''}`}
            >
              {isFood && <div className="food-logo">N</div>}
            </div>
          );
        })}
      </div>

      {!gameStarted && !gameOver && (
        <div className="game-overlay">
          <div className="game-message">
            <h2>Press any key to start</h2>
            <p>Use arrow keys to move</p>
            <p>Press ESC to exit</p>
          </div>
        </div>
      )}

      {gameOver && (
        <div className="game-overlay">
          <div className="game-message">
            <h2>GAME OVER</h2>
            <p>Final Score: {score}</p>
            <div className="game-buttons">
              <button onClick={restartGame} className="game-button">Play Again</button>
              <button onClick={handleGameEnd} className="game-button">Back to Homepage</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SnakeGame;